import { ActionReducer, createReducer, on } from '@ngrx/store';
import { DashboardInstallationData } from '@vi-iot/client';
import {
  addAdditionalFlagsToColumn,
  DashboardInstallationColumn,
  sortColumnsByNeighbor,
} from '../kanban-board';
import { DashboardActions } from './dashboard.actions';
import {
  DashboardInstallationState,
  DashboardTileOrder,
  DashboardTileType,
  dashboardTileTypeToDashboardTileObjectMapper,
  defaultDashboardTilesOrder,
  InstallationWithAnomaliesTabsType,
  InstallationWithFaultTabsType,
  LastConnectedSystemTabsType,
} from './dashboard.model';
import { DashboardState } from './dashboard.state';

export const initDashboardState: DashboardState = {
  columns: null,
  tilesOrder: {
    value: [],
    loading: false,
    error: null,
    reloadGrid: false,
  },
  fetchingDashboardStatus: {
    loading: false,
    loaded: false,
    error: null,
  },
  installationsWithFault: {
    value: {
      [InstallationWithFaultTabsType.All]: {
        value: [],
        installationsCount: null,
        canFetch: true,
        loading: false,
        error: null,
      },
      [InstallationWithFaultTabsType.Error]: {
        value: [],
        installationsCount: null,
        canFetch: true,
        loading: false,
        error: null,
      },
      [InstallationWithFaultTabsType.Locked]: {
        value: [],
        installationsCount: null,
        canFetch: true,
        loading: false,
        error: null,
      },
    },
    loading: false,
    error: null,
  },
  upcomingMaintenanceForInstallations: {
    value: [],
    installationsCount: 0,
    canFetch: true,
    loading: false,
    initializing: false,
    error: null,
  },
  lastConnectedSystems: {
    value: {
      [LastConnectedSystemTabsType.Today]: {
        value: [],
        installationsCount: 0,
        canFetch: true,
        loading: false,
        error: null,
      },
      [LastConnectedSystemTabsType.Yesterday]: {
        value: [],
        installationsCount: 0,
        canFetch: true,
        loading: false,
        error: null,
      },
      [LastConnectedSystemTabsType.Last7Days]: {
        value: [],
        installationsCount: 0,
        canFetch: true,
        loading: false,
        error: null,
      },
      [LastConnectedSystemTabsType.Last30Days]: {
        value: [],
        installationsCount: 0,
        canFetch: true,
        loading: false,
        error: null,
      },
      [LastConnectedSystemTabsType.LastYear]: {
        value: [],
        installationsCount: 0,
        canFetch: true,
        loading: false,
        error: null,
      },
    },
    loading: false,
    error: null,
  },
  installationsWithoutHems: {
    value: [],
    installationsCount: 0,
    canFetch: true,
    loading: false,
    initializing: false,
    error: null,
  },
  installationsWithAnomalies: {
    value: {
      [InstallationWithAnomaliesTabsType.All]: {
        value: [],
        installationsCount: 0,
        canFetch: true,
        loading: false,
        error: null,
      },
      [InstallationWithAnomaliesTabsType.Warning]: {
        value: [],
        installationsCount: 0,
        canFetch: true,
        loading: false,
        error: null,
      },
      [InstallationWithAnomaliesTabsType.Information]: {
        value: [],
        installationsCount: 0,
        canFetch: true,
        loading: false,
        error: null,
      },
    },
    loading: false,
    error: null,
  },
  installationsWithoutSRC: {
    value: [],
    installationsCount: 0,
    canFetch: true,
    loading: false,
    initializing: false,
    error: null,
  },
  contractsEndingSoon: {
    value: [],
    installationsCount: 0,
    canFetch: true,
    loading: false,
    initializing: false,
    error: null,
  },
  devicesWithLowWifi: {
    value: [],
    installationsCount: 0,
    canFetch: true,
    loading: false,
    initializing: false,
    error: null,
  },
  isWhatsAppBannerVisible: false,
};

export const dashboardReducer: ActionReducer<DashboardState> = createReducer(
  initDashboardState,

  on(
    DashboardActions.fetchTilesOrder.attempt,
    (): DashboardState => ({
      ...initDashboardState,
      tilesOrder: {
        value: [],
        loading: true,
        error: null,
        reloadGrid: true,
      },
    })
  ),

  on(
    DashboardActions.fetchTilesOrder.success,
    (state, { tilesOrder }): DashboardState => {
      const allTiles = defaultDashboardTilesOrder;
      let mergedTiles;

      if (tilesOrder.length > 0) {
        const missingTiles = allTiles.filter((allTile: DashboardTileOrder) => {
          return !tilesOrder.some((tileOrder) => tileOrder.type === allTile.type);
        });
        mergedTiles = tilesOrder.concat(missingTiles);
      } else {
        mergedTiles = allTiles;
      }

      const tilesToShow = mergedTiles.filter((mergedTile: DashboardTileOrder) => {
        return allTiles.some((allTile: DashboardTileOrder) => allTile.type === mergedTile.type);
      });

      return {
        ...state,
        tilesOrder: {
          value: tilesToShow,
          loading: false,
          error: null,
          reloadGrid: false,
        },
      };
    }
  ),

  on(
    DashboardActions.fetchTilesOrder.error,
    (state, { error }): DashboardState => ({
      ...state,
      tilesOrder: {
        value: state.tilesOrder.value,
        loading: false,
        error,
        reloadGrid: false,
      },
    })
  ),

  on(
    DashboardActions.saveTilesOrder.attempt,
    (state, { tilesOrder, reloadGrid }): DashboardState => ({
      ...state,
      tilesOrder: {
        value: tilesOrder,
        loading: true,
        error: null,
        reloadGrid,
      },
    })
  ),

  on(
    DashboardActions.saveTilesOrder.success,
    (state): DashboardState => ({
      ...state,
      tilesOrder: {
        value: state.tilesOrder.value,
        loading: false,
        error: null,
        reloadGrid: false,
      },
    })
  ),

  on(
    DashboardActions.saveTilesOrder.error,
    (state, { error }): DashboardState => ({
      ...state,
      tilesOrder: {
        value: state.tilesOrder.value,
        loading: false,
        error,
        reloadGrid: false,
      },
    })
  ),

  on(
    DashboardActions.getDashboard.attempt,
    (state): DashboardState => ({
      ...state,
      fetchingDashboardStatus: { loading: true, loaded: false, error: null },
    })
  ),

  on(
    DashboardActions.getDashboard.success,
    (state, { columns }): DashboardState => {
      const sortedColumns: DashboardInstallationColumn[] = sortColumnsByNeighbor(
        structuredClone(columns)
      ).map(addAdditionalFlagsToColumn);

      return {
        ...state,
        fetchingDashboardStatus: { loading: false, loaded: true, error: null },
        columns: sortedColumns,
      };
    }
  ),

  on(
    DashboardActions.getDashboard.error,
    (state, { error }): DashboardState => ({
      ...state,
      fetchingDashboardStatus: { loading: false, loaded: false, error },
    })
  ),

  on(
    DashboardActions.installationsForSingleTileType.init,
    (state, { tileType, tab }): DashboardState => {
      const tileStateName = dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState;

      if (tab) {
        return {
          ...state,
          [tileStateName]: {
            ...state[tileStateName],
            value: {
              ...state[tileStateName].value,
              [tab]: {
                ...state[tileStateName].value[tab],
                initializing: true,
                loading: true,
                error: null,
              },
            },
          },
        };
      }

      return {
        ...state,
        [tileStateName]: {
          ...state[tileStateName],
          loading: true,
          initializing: true,
          error: null,
        },
      };
    }
  ),

  on(
    DashboardActions.installationsForSingleTileType.fetch,
    (state, { tileType, tab }): DashboardState => {
      const tileStateName = dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState;

      if (tab) {
        return {
          ...state,
          [tileStateName]: {
            ...state[tileStateName],
            value: {
              ...state[tileStateName].value,
              [tab]: {
                ...state[tileStateName].value[tab],
                loading: true,
                error: null,
              },
            },
          },
        };
      }

      return {
        ...state,
        [tileStateName]: {
          ...state[tileStateName],
          loading: true,
          error: null,
        },
      };
    }
  ),

  on(
    DashboardActions.installationsForSingleTileType.success,
    (state, { tileType, tab, installations }): DashboardState => {
      const tileStateName = dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState;

      let installationState: DashboardInstallationState = tab
        ? structuredClone(state[tileStateName].value[tab])
        : structuredClone(state[tileStateName]);

      const concatInstallations: DashboardInstallationData[] = installationState.value.concat(
        installations
      );

      if (concatInstallations.length > installationState.value.length) {
        installationState.value = concatInstallations;
        installationState.installationsCount =
          installationState.installationsCount === null
            ? concatInstallations.length
            : installationState.installationsCount;
        installationState.canFetch =
          concatInstallations.length < installationState.installationsCount;
      } else {
        installationState.canFetch = false;
      }
      installationState.loading = false;
      installationState.initializing = false;

      if (tab) {
        return {
          ...state,
          [tileStateName]: {
            ...state[tileStateName],
            value: {
              ...state[tileStateName].value,
              [tab]: installationState,
            },
          },
        };
      }

      return {
        ...state,
        [tileStateName]: installationState,
      };
    }
  ),

  on(
    DashboardActions.installationsForSingleTileType.noDataToFetch,
    (state, { tileType, tab }): DashboardState => {
      const tileStateName = dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState;

      if (tab) {
        return {
          ...state,
          [tileStateName]: {
            ...state[tileStateName],
            value: {
              ...state[tileStateName].value,
              [tab]: {
                ...state[tileStateName].value[tab],
                initializing: false,
                loading: false,
                canFetch: false,
              },
            },
          },
        };
      }

      return {
        ...state,
        [tileStateName]: {
          ...state[tileStateName],
          initializing: false,
          loading: false,
          canFetch: false,
        },
      };
    }
  ),

  on(
    DashboardActions.installationsForSingleTileType.error,
    (state, { tileType, tab, error }): DashboardState => {
      const tileStateName = dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState;

      if (tab) {
        return {
          ...state,
          [tileStateName]: {
            ...state[tileStateName],
            value: {
              ...state[tileStateName].value,
              [tab]: {
                ...state[tileStateName].value[tab],
                initializing: false,
                loading: false,
                canFetch: false,
                error,
              },
            },
          },
        };
      }

      return {
        ...state,
        [tileStateName]: {
          ...state[tileStateName],
          initializing: false,
          loading: false,
          canFetch: false,
          error,
        },
      };
    }
  ),

  on(
    DashboardActions.initInstallationsForMultipleTileTypes.attempt,
    (state, { tileTypes }): DashboardState => {
      let newState: DashboardState = { ...state };
      tileTypes.forEach((tileType) => {
        const tileStateName = dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState;
        if (dashboardTileTypeToDashboardTileObjectMapper[tileType].tabs) {
          dashboardTileTypeToDashboardTileObjectMapper[tileType].tabs.forEach((tab) => {
            newState = {
              ...newState,
              [tileStateName]: {
                ...newState[tileStateName],
                value: {
                  ...newState[tileStateName].value,
                  [tab.storeState]: {
                    ...newState[tileStateName].value[tab.storeState],
                    initializing: true,
                    loading: true,
                    canFetch: true,
                    error: null,
                  },
                },
                initializing: true,
                loading: true,
                error: null,
              },
            };
          });
        } else {
          newState = {
            ...newState,
            [tileStateName]: {
              ...newState[tileStateName],
              initializing: true,
              loading: true,
              canFetch: true,
              error: null,
            },
          };
        }
      });

      return newState;
    }
  ),

  on(
    DashboardActions.initInstallationsForMultipleTileTypes.complete,
    (state, { data }): DashboardState => {
      let newState: DashboardState = { ...state };
      data.forEach((item) => {
        const installationsCount = item.installations?.length ?? 0;
        const tileStateName =
          dashboardTileTypeToDashboardTileObjectMapper[item.tileType].storeState;

        if (item.tab) {
          newState = {
            ...newState,
            [tileStateName]: {
              ...newState[tileStateName],
              value: {
                ...newState[tileStateName].value,
                [item.tab]: {
                  ...newState[tileStateName].value[item.tab],
                  value: item.installations,
                  installationsCount:
                    state[tileStateName].value[item.tab].installationsCount === null
                      ? installationsCount
                      : state[tileStateName].value[item.tab].installationsCount,
                  initializing: false,
                  loading: false,
                  error: item.error,
                  canFetch:
                    item.installations.length <
                    newState[tileStateName].value[item.tab].installationsCount,
                },
              },
              initializing: false,
              loading: false,
            },
          };
        } else {
          newState = {
            ...newState,
            [tileStateName]: {
              ...newState[tileStateName],
              value: item.installations,
              installationsCount:
                state[tileStateName].installationsCount === null
                  ? installationsCount
                  : state[tileStateName].installationsCount,
              initializing: false,
              loading: false,
              error: item.error,
              canFetch: item.installations.length < newState[tileStateName].installationsCount,
            },
          };
        }
      });

      return newState;
    }
  ),

  on(
    DashboardActions.contractEndingSoon.init,
    (state): DashboardState => ({
      ...state,
      contractsEndingSoon: {
        value: [],
        installationsCount: 0,
        canFetch: true,
        loading: true,
        initializing: true,
        error: null,
      },
    })
  ),

  on(
    DashboardActions.contractEndingSoon.fetch,
    (state): DashboardState => ({
      ...state,
      contractsEndingSoon: {
        ...state.contractsEndingSoon,
        loading: true,
        error: null,
      },
    })
  ),

  on(
    DashboardActions.contractEndingSoon.success,
    (state, { installations }): DashboardState => {
      const tileStateName =
        dashboardTileTypeToDashboardTileObjectMapper[DashboardTileType.ContractsEndingSoon]
          .storeState;
      let installationState: DashboardInstallationState = structuredClone(state[tileStateName]);

      const concatInstallations: DashboardInstallationData[] = installationState.value.concat(
        installations
      );

      if (concatInstallations.length > installationState.value.length) {
        installationState.value = concatInstallations;
        installationState.installationsCount =
          installationState.installationsCount === null
            ? concatInstallations.length
            : installationState.installationsCount;
        installationState.canFetch =
          concatInstallations.length < installationState.installationsCount;
      } else {
        installationState.canFetch = false;
      }
      installationState.loading = false;
      installationState.initializing = false;

      return {
        ...state,
        [tileStateName]: installationState,
      };
    }
  ),

  on(
    DashboardActions.contractEndingSoon.noDataToFetch,
    (state): DashboardState => ({
      ...state,
      contractsEndingSoon: {
        ...state.contractsEndingSoon,
        initializing: false,
        loading: false,
        canFetch: false,
      },
    })
  ),

  on(
    DashboardActions.contractEndingSoon.error,
    (state, { error }): DashboardState => ({
      ...state,
      contractsEndingSoon: {
        ...state.contractsEndingSoon,
        initializing: false,
        loading: false,
        canFetch: false,
        error,
      },
    })
  ),

  on(
    DashboardActions.countInstallationsForTiles.success,
    (state, { installationsSums }): DashboardState => {
      let newState: DashboardState = { ...state };
      installationsSums.forEach((item) => {
        if (item.tab) {
          newState = {
            ...newState,
            [item.tileType]: {
              ...newState[item.tileType],
              value: {
                ...newState[item.tileType].value,
                [item.tab]: {
                  ...newState[item.tileType].value[item.tab],
                  installationsCount: item.counter,
                  canFetch: state[item.tileType].value[item.tab].value.length < item.counter,
                },
              },
            },
          };
        } else {
          newState = {
            ...newState,
            [item.tileType]: {
              ...newState[item.tileType],
              installationsCount: item.counter,
              canFetch: state[item.tileType].value.length < item.counter,
            },
          };
        }
      });

      return newState;
    }
  ),

  on(
    DashboardActions.whatsAppBannerInfo.success,
    (state, { data }): DashboardState => {
      return {
        ...state,
        isWhatsAppBannerVisible: data,
      };
    }
  )
);
